import { inject, ref, type InjectionKey, type Ref } from 'vue';
import type { IconName, Maybe } from '@/types';
import type { Address } from '@/types/customer/address.types';

/** Catégorie d'une adresse, déterminant l'onglet et l'icône associée. */
export type AddressCategory = 'home' | 'favorite' | 'recent' | 'search' | 'restricted';

/** Propriétés communes à tous les panneaux de sélection d'adresse */
export type ITabPanelControllerProps = {
  /** Adresse sélectionnée par l'utilisateur, le cas échéant */
  initialValue?: Maybe<Address>;
  searchText: string;
  /** Nombre de caractères minimal avant le déclenchement de la recherche d'adresse */
  searchMinLength: number;
};

/**
 * Libellé optionnel affiché à droite de chaque item d'une liste d'adresses
 *  dans la gestion des lieux favoris. Fourni par le contexte parent
 * concerné et injecté par AddressList.
 */
export const ADDRESS_ITEM_ACTION_LABEL_KEY: InjectionKey<Ref<string | undefined>> = Symbol();

/** Injecteur pour le libellé "décoratif" ajouté à chaque adresse */
export function injectAddressItemActionLabel(): Ref<string | undefined> {
  return inject(ADDRESS_ITEM_ACTION_LABEL_KEY, ref());
}

export const categoryToSvgIconNameMap: Record<AddressCategory, IconName> = {
  home: 'home',
  favorite: 'star',
  recent: 'clock',
  search: 'magnifying-glass',
  restricted: 'pin-outline',
};

export const landmarkTypeToSvgIconNameMap = {
  AÉROPORT: 'aeroport',
  ARDI: 'batiment',
  ARENA: 'arena',
  ASSOC: 'batiment',
  BIBLIOTH: 'bibliotheque',
  BUREAU: 'batiment',
  CASINO: 'casino',
  CDJ: 'batiment',
  CEA: 'ecole',
  CEGEP: 'ecole',
  CHSLD: 'hopital',
  CIMETIER: 'cimetiere',
  CINEMA: 'cinema',
  CLSC: 'batiment',
  COLLEGE: 'ecole',
  CTRACHAT: 'magasin',
  DOMICILE: 'domicile' /* Pas un landmark officiel, mais utilisé pour identifier l'adresse du domicile */,
  ECOLE: 'ecole',
  EVEN: 'general',
  GARE: 'gare-de-train',
  GOUV: 'batiment',
  HOPITAL: 'hopital',
  HOTEL: 'hotel',
  LOISIRS: 'general',
  MAGASIN: 'magasin',
  MARCHE: 'nourriture',
  MEDIA: 'batiment',
  MEDICAL: 'hopital',
  METRO: 'station-de-metro',
  METROLON: 'station-de-metro',
  METROLVL: 'station-de-metro',
  MUSEE: 'musee',
  PARC: 'parc',
  PISCINE: 'piscine',
  RELIGION: 'general',
  REM: 'station-rem',
  RESIDENC: 'batiment',
  RESTO: 'restaurant',
  STADE: 'stade',
  STM: 'batiment',
  THEATRE: 'theatre',
  UNVERSIT: 'ecole',
  VACCIN: 'vaccination',
  YMCA: 'sport',
} as const;
